/**
 * Messages of the shared primitives (owner ui-primitives, plan §8.9).
 *
 * One `<area>En` / `<area>De` pair per primitive. The pairs are plain object literals, so a bundle
 * only contains the pairs of the primitives it renders: each component registers its own pair when
 * its module loads (`registerPrimitiveMessages`). `de` must have every key of `en` (type-checked).
 * `{name}` placeholders are interpolated; keys ending in `_one` / `_other` are chosen by `count`.
 */
import { type UiMessages, registerUiMessages } from './index';

type De<E> = Record<keyof E, string>;

export const listEn = {
  'ui.list.loading': 'Loading…',
  'ui.list.loadingMore': 'Loading more…',
  'ui.list.error': 'Couldn’t load the list',
  'ui.list.retry': 'Try again',
  'ui.list.results_one': '{count} result',
  'ui.list.results_other': '{count} results',
  'ui.list.noResults': 'No results',
} as const;
export const listDe: De<typeof listEn> = {
  'ui.list.loading': 'Wird geladen…',
  'ui.list.loadingMore': 'Weitere werden geladen…',
  'ui.list.error': 'Die Liste konnte nicht geladen werden',
  'ui.list.retry': 'Erneut versuchen',
  'ui.list.results_one': '{count} Ergebnis',
  'ui.list.results_other': '{count} Ergebnisse',
  'ui.list.noResults': 'Keine Ergebnisse',
};

export const comboboxEn = {
  'ui.combobox.placeholder': 'Choose…',
  'ui.combobox.search': 'Search {label}',
  'ui.combobox.searchPlaceholder': 'Search…',
  'ui.combobox.empty': 'No options yet',
  'ui.combobox.noMatch': 'Nothing matches “{query}”',
  'ui.combobox.loading': 'Loading options…',
  'ui.combobox.error': 'Couldn’t load the options',
  'ui.combobox.retry': 'Try again',
  'ui.combobox.create': 'Create “{query}”',
  'ui.combobox.creating': 'Creating “{query}”…',
  'ui.combobox.recent': 'Recent',
  'ui.combobox.clear': 'Clear',
  'ui.combobox.clearLabel': 'Clear {label}',
  'ui.combobox.done': 'Done',
  'ui.combobox.selected_one': '{count} selected',
  'ui.combobox.selected_other': '{count} selected',
  'ui.combobox.more': '+{count}',
  'ui.combobox.hint': '↑↓ to move · ↵ to choose',
  'ui.combobox.hintMulti': '↑↓ to move · ↵ to toggle · Esc to finish',
} as const;
export const comboboxDe: De<typeof comboboxEn> = {
  'ui.combobox.placeholder': 'Auswählen…',
  'ui.combobox.search': '{label} durchsuchen',
  'ui.combobox.searchPlaceholder': 'Suchen…',
  'ui.combobox.empty': 'Noch keine Optionen',
  'ui.combobox.noMatch': 'Nichts passt zu „{query}“',
  'ui.combobox.loading': 'Optionen werden geladen…',
  'ui.combobox.error': 'Die Optionen konnten nicht geladen werden',
  'ui.combobox.retry': 'Erneut versuchen',
  'ui.combobox.create': '„{query}“ anlegen',
  'ui.combobox.creating': '„{query}“ wird angelegt…',
  'ui.combobox.recent': 'Zuletzt verwendet',
  'ui.combobox.clear': 'Leeren',
  'ui.combobox.clearLabel': '{label} leeren',
  'ui.combobox.done': 'Fertig',
  'ui.combobox.selected_one': '{count} ausgewählt',
  'ui.combobox.selected_other': '{count} ausgewählt',
  'ui.combobox.more': '+{count}',
  'ui.combobox.hint': '↑↓ wechseln · ↵ auswählen',
  'ui.combobox.hintMulti': '↑↓ wechseln · ↵ an/aus · Esc fertig',
};

export const commandEn = {
  'ui.command.placeholder': 'Type a command or search…',
  'ui.command.search': 'Search commands',
  'ui.command.empty': 'No results for “{query}”',
  'ui.command.emptyPage': 'Nothing here yet',
  'ui.command.back': 'Back',
  'ui.command.path': 'Location',
  'ui.command.hintMove': 'to move',
  'ui.command.hintRun': 'to run',
  'ui.command.hintOpen': 'to open',
  'ui.command.hintBack': 'to go back',
  'ui.command.hintClose': 'to close',
} as const;
export const commandDe: De<typeof commandEn> = {
  'ui.command.placeholder': 'Befehl eingeben oder suchen…',
  'ui.command.search': 'Befehle durchsuchen',
  'ui.command.empty': 'Keine Ergebnisse für „{query}“',
  'ui.command.emptyPage': 'Hier ist noch nichts',
  'ui.command.back': 'Zurück',
  'ui.command.path': 'Ort',
  'ui.command.hintMove': 'wechseln',
  'ui.command.hintRun': 'ausführen',
  'ui.command.hintOpen': 'öffnen',
  'ui.command.hintBack': 'zurück',
  'ui.command.hintClose': 'schließen',
};

export const treeEn = {
  'ui.tree.expand': 'Expand {label}',
  'ui.tree.collapse': 'Collapse {label}',
  'ui.tree.rename': 'New name for {label}',
  'ui.tree.empty': 'Nothing here yet',
  'ui.tree.loading': 'Loading…',
  'ui.tree.moved': 'Moved {label}',
  'ui.tree.renamed': 'Renamed to {label}',
  'ui.tree.selected_one': '{count} item selected',
  'ui.tree.selected_other': '{count} items selected',
  'ui.tree.moveUp': 'Move up',
  'ui.tree.moveDown': 'Move down',
  'ui.tree.moveInto': 'Move into the item above',
  'ui.tree.moveOut': 'Move out of the parent',
} as const;
export const treeDe: De<typeof treeEn> = {
  'ui.tree.expand': '{label} aufklappen',
  'ui.tree.collapse': '{label} zuklappen',
  'ui.tree.rename': 'Neuer Name für {label}',
  'ui.tree.empty': 'Hier ist noch nichts',
  'ui.tree.loading': 'Wird geladen…',
  'ui.tree.moved': '{label} verschoben',
  'ui.tree.renamed': 'Umbenannt in {label}',
  'ui.tree.selected_one': '{count} Element ausgewählt',
  'ui.tree.selected_other': '{count} Elemente ausgewählt',
  'ui.tree.moveUp': 'Nach oben verschieben',
  'ui.tree.moveDown': 'Nach unten verschieben',
  'ui.tree.moveInto': 'In das Element darüber verschieben',
  'ui.tree.moveOut': 'Aus dem übergeordneten Element verschieben',
};

export const paneEn = {
  'ui.pane.resize': 'Resize {label}',
  'ui.pane.collapse': 'Collapse {label}',
  'ui.pane.expand': 'Expand {label}',
  'ui.inspector.label': 'Inspector',
  'ui.inspector.hide': 'Hide inspector',
  'ui.inspector.show': 'Show inspector',
  'ui.inspector.resize': 'Resize inspector',
  'ui.inspector.empty': 'Select something to see its properties',
  'ui.inspector.mixed': 'Mixed',
  'ui.inspector.selection_one': '{count} item',
  'ui.inspector.selection_other': '{count} items',
} as const;
export const paneDe: De<typeof paneEn> = {
  'ui.pane.resize': 'Größe von {label} ändern',
  'ui.pane.collapse': '{label} einklappen',
  'ui.pane.expand': '{label} ausklappen',
  'ui.inspector.label': 'Inspektor',
  'ui.inspector.hide': 'Inspektor ausblenden',
  'ui.inspector.show': 'Inspektor einblenden',
  'ui.inspector.resize': 'Breite des Inspektors ändern',
  'ui.inspector.empty': 'Wählen Sie etwas aus, um seine Eigenschaften zu sehen',
  'ui.inspector.mixed': 'Gemischt',
  'ui.inspector.selection_one': '{count} Element',
  'ui.inspector.selection_other': '{count} Elemente',
};

export const actionsEn = {
  'ui.splitButton.more': 'More options for {label}',
  'ui.toolbar.more': 'More tools',
  'ui.tabs.more': 'More tabs',
  'ui.tabs.moreCount': '{count} more',
  'ui.tabs.close': 'Close {label}',
  'ui.tabs.moved': '{label} moved to position {position}',
  'ui.pageHeader.crumbs': 'Breadcrumb',
  'ui.pageHeader.more': 'More actions',
  'ui.disclosure.show': 'Show {label}',
} as const;
export const actionsDe: De<typeof actionsEn> = {
  'ui.splitButton.more': 'Weitere Optionen für {label}',
  'ui.toolbar.more': 'Weitere Werkzeuge',
  'ui.tabs.more': 'Weitere Tabs',
  'ui.tabs.moreCount': '{count} weitere',
  'ui.tabs.close': '{label} schließen',
  'ui.tabs.moved': '{label} an Position {position} verschoben',
  'ui.pageHeader.crumbs': 'Brotkrumen',
  'ui.pageHeader.more': 'Weitere Aktionen',
  'ui.disclosure.show': '{label} anzeigen',
};

export const statesEn = {
  'ui.error.title': 'Something went wrong',
  'ui.error.body': 'This couldn’t be loaded. Try again in a moment.',
  'ui.error.offlineTitle': 'You’re offline',
  'ui.error.offlineBody': 'Check your connection. We’ll load this as soon as you’re back online.',
  'ui.error.retry': 'Try again',
  'ui.error.copy': 'Copy details',
  'ui.error.copied': 'Details copied',
  'ui.error.reference': 'Reference {id}',
  'ui.notFound.title': 'This page doesn’t exist',
  'ui.notFound.body': 'It may have been moved or deleted, or the link is incomplete.',
  'ui.noAccess.title': 'You don’t have access to this',
  'ui.noAccess.body': 'Ask an admin of this workspace to give you access.',
  'ui.noAccess.admins': 'Admins of this workspace',
  'ui.noAccess.contact': 'Write to {name}',
  'ui.noAccess.noAdmins': 'Ask the person who invited you to give you access.',
  'ui.settings.readOnly':
    'You can view these settings. Only members with permission to change them can edit.',
  'ui.settings.checking': 'Checking access…',
  'ui.avatars.more_one': '{count} more',
  'ui.avatars.more_other': '{count} more',
  'ui.avatars.group_one': '{count} person: {names}',
  'ui.avatars.group_other': '{count} people: {names}',
} as const;
export const statesDe: De<typeof statesEn> = {
  'ui.error.title': 'Etwas ist schiefgelaufen',
  'ui.error.body': 'Das konnte nicht geladen werden. Versuchen Sie es gleich noch einmal.',
  'ui.error.offlineTitle': 'Sie sind offline',
  'ui.error.offlineBody': 'Prüfen Sie Ihre Verbindung. Sobald Sie wieder online sind, laden wir das.',
  'ui.error.retry': 'Erneut versuchen',
  'ui.error.copy': 'Details kopieren',
  'ui.error.copied': 'Details kopiert',
  'ui.error.reference': 'Referenz {id}',
  'ui.notFound.title': 'Diese Seite gibt es nicht',
  'ui.notFound.body': 'Sie wurde verschoben oder gelöscht, oder der Link ist unvollständig.',
  'ui.noAccess.title': 'Sie haben keinen Zugriff darauf',
  'ui.noAccess.body': 'Bitten Sie einen Admin dieses Arbeitsbereichs, Ihnen Zugriff zu geben.',
  'ui.noAccess.admins': 'Admins dieses Arbeitsbereichs',
  'ui.noAccess.contact': 'An {name} schreiben',
  'ui.noAccess.noAdmins': 'Bitten Sie die Person, die Sie eingeladen hat, Ihnen Zugriff zu geben.',
  'ui.settings.readOnly':
    'Sie können diese Einstellungen ansehen. Ändern können sie nur Mitglieder mit der passenden Berechtigung.',
  'ui.settings.checking': 'Zugriff wird geprüft…',
  'ui.avatars.more_one': '{count} weitere',
  'ui.avatars.more_other': '{count} weitere',
  'ui.avatars.group_one': '{count} Person: {names}',
  'ui.avatars.group_other': '{count} Personen: {names}',
};

export const propertyEn = {
  'ui.property.mixed': 'Mixed',
  'ui.property.invalid': 'This value isn’t allowed here',
  'ui.property.on': 'On',
  'ui.property.off': 'Off',
  'ui.number.invalid': 'Enter a number, e.g. 12 or 12+4',
  'ui.number.unit': 'Can’t use {unit} here',
  'ui.number.mixedRelative': 'Enter a number for all selected items',
  'ui.number.range': 'Between {min} and {max}',
  'ui.number.min': 'At least {min}',
  'ui.number.max': 'At most {max}',
  'ui.number.scrub': 'Drag to change {label}',
  'ui.color.choose': 'Choose a colour for {label}',
  'ui.color.none': 'None',
  'ui.color.theme': 'Theme',
  'ui.color.recent': 'Recent',
  'ui.color.custom': 'Custom',
  'ui.color.hex': 'Hex colour',
  'ui.color.opacity': 'Opacity',
  'ui.color.eyedropper': 'Pick a colour from the screen',
  'ui.color.saturation': 'Saturation and brightness',
  'ui.color.saturationValue': 'Saturation {s} %, brightness {v} %',
  'ui.color.hue': 'Hue',
  'ui.color.contrast': 'Contrast {ratio}:1',
  'ui.color.contrastGood': 'Readable (AA)',
  'ui.color.contrastGreat': 'Very readable (AAA)',
  'ui.color.contrastLow': 'Hard to read',
  'ui.color.invalidHex': 'Use a hex colour like #444CE7',
  'ui.color.sample': 'Aa',
  'ui.font.choose': 'Choose a font',
  'ui.font.search': 'Search fonts…',
  'ui.font.default': 'Default font',
  'ui.font.brand': 'Brand',
  'ui.font.sans': 'Sans serif',
  'ui.font.serif': 'Serif',
  'ui.font.mono': 'Monospace',
  'ui.font.display': 'Display',
  'ui.font.handwriting': 'Handwriting',
  'ui.font.other': 'Other',
  'ui.font.preview': 'The quick brown fox',
} as const;
export const propertyDe: De<typeof propertyEn> = {
  'ui.property.mixed': 'Gemischt',
  'ui.property.invalid': 'Dieser Wert ist hier nicht erlaubt',
  'ui.property.on': 'An',
  'ui.property.off': 'Aus',
  'ui.number.invalid': 'Geben Sie eine Zahl ein, z. B. 12 oder 12+4',
  'ui.number.unit': '{unit} ist hier nicht möglich',
  'ui.number.mixedRelative': 'Geben Sie eine Zahl für alle ausgewählten Elemente ein',
  'ui.number.range': 'Zwischen {min} und {max}',
  'ui.number.min': 'Mindestens {min}',
  'ui.number.max': 'Höchstens {max}',
  'ui.number.scrub': 'Ziehen, um {label} zu ändern',
  'ui.color.choose': 'Farbe für {label} wählen',
  'ui.color.none': 'Keine',
  'ui.color.theme': 'Design',
  'ui.color.recent': 'Zuletzt verwendet',
  'ui.color.custom': 'Eigene',
  'ui.color.hex': 'Hex-Farbe',
  'ui.color.opacity': 'Deckkraft',
  'ui.color.eyedropper': 'Farbe vom Bildschirm aufnehmen',
  'ui.color.saturation': 'Sättigung und Helligkeit',
  'ui.color.saturationValue': 'Sättigung {s} %, Helligkeit {v} %',
  'ui.color.hue': 'Farbton',
  'ui.color.contrast': 'Kontrast {ratio}:1',
  'ui.color.contrastGood': 'Gut lesbar (AA)',
  'ui.color.contrastGreat': 'Sehr gut lesbar (AAA)',
  'ui.color.contrastLow': 'Schwer lesbar',
  'ui.color.invalidHex': 'Verwenden Sie eine Hex-Farbe wie #444CE7',
  'ui.color.sample': 'Aa',
  'ui.font.choose': 'Schrift wählen',
  'ui.font.search': 'Schriften durchsuchen…',
  'ui.font.default': 'Standardschrift',
  'ui.font.brand': 'Marke',
  'ui.font.sans': 'Serifenlos',
  'ui.font.serif': 'Serif',
  'ui.font.mono': 'Monospace',
  'ui.font.display': 'Display',
  'ui.font.handwriting': 'Handschrift',
  'ui.font.other': 'Weitere',
  'ui.font.preview': 'Franz jagt im komplett verwahrlosten Taxi',
};

/** Key caps (`Kbd`): the word between the steps of a sequence, as `formatShortcut` writes it. */
export const kbdEn = {
  'ui.kbd.then': 'then',
} as const;
export const kbdDe: De<typeof kbdEn> = {
  'ui.kbd.then': 'dann',
};

/** Every primitive message key (the kit's `UiMessageKey` includes these). */
export type PrimitiveMessageKey =
  | keyof typeof listEn
  | keyof typeof comboboxEn
  | keyof typeof commandEn
  | keyof typeof treeEn
  | keyof typeof paneEn
  | keyof typeof actionsEn
  | keyof typeof statesEn
  | keyof typeof propertyEn
  | keyof typeof kbdEn;

/** Every pair, for parity tests and tools (importing it pulls in every string). */
export const ALL_PRIMITIVE_MESSAGES = [
  [listEn, listDe],
  [comboboxEn, comboboxDe],
  [commandEn, commandDe],
  [treeEn, treeDe],
  [paneEn, paneDe],
  [actionsEn, actionsDe],
  [statesEn, statesDe],
  [propertyEn, propertyDe],
  [kbdEn, kbdDe],
] as const;

/** Register one pair (call at module level of the component that uses it). */
export function registerPrimitiveMessages<E extends Record<string, string>>(en: E, de: De<E>): void {
  registerUiMessages('en', en as Partial<UiMessages>);
  registerUiMessages('de', de as Partial<UiMessages>);
}
